<script setup lang="ts">
import { LucideArrowDown } from 'lucide-vue-next'
import type { BankingFactBlock, IBankingFact } from './types'
import { useBankingNumber } from './useBankingNumber'

const props = defineProps<{
  facts: IBankingFact[]
  /** Blocks the page renders: a fact links to its block only when the block is on the page. */
  blocks: readonly BankingFactBlock[]
}>()

const MAX_FACTS = 3

/** Facts that say the same as the headline of their block, so they are left out when it is on the page. */
const HEADLINE_FACTS: Partial<Record<string, BankingFactBlock>> = {
  region_ratio: 'regions'
}

const { t, te } = useI18n()
const { formatPercent, formatNumber } = useBankingNumber()

interface IFactCard {
  key: string
  block: string
  text: string
  isLinked: boolean
}

/** A translation key that exists in the current locale or in Russian, the fallback locale. */
const known = (key: string): boolean => te(key) || te(key, 'ru')

const numberParam = (fact: IBankingFact, name: string): number | null => {
  const value = fact.params[name]

  return typeof value === 'number' ? value : null
}

const seriesName = (fact: IBankingFact): string | null => {
  const key = `banking_statistics.series.${fact.params.series}`

  return known(key) ? t(key) : null
}

const signed = (value: number, text: string): string =>
  `${value > 0 ? '+' : value < 0 ? '−' : ''}${text}`

/** The sentence of a fact, or null when the fact is of an unknown kind or misses a param. */
const factText = (fact: IBankingFact): string | null => {
  switch (fact.key) {
    case 'record_low':
    case 'record_high': {
      const series = seriesName(fact)
      const value = numberParam(fact, 'value')
      const sinceYear = numberParam(fact, 'since_year')

      if (!series || value === null || sinceYear === null) return null

      return t(`banking_statistics.facts.${fact.key}`, {
        series,
        value: formatPercent(value),
        since_year: sinceYear
      })
    }
    case 'yoy_change': {
      const series = seriesName(fact)
      const change = numberParam(fact, 'change')

      if (!series || change === null || change === 0) return null

      return t(`banking_statistics.facts.yoy_change_${change > 0 ? 'up' : 'down'}`, {
        series,
        change: `${formatNumber(Math.abs(change), 1)}%`
      })
    }
    case 'reversal': {
      const series = seriesName(fact)
      const change = numberParam(fact, 'change')

      if (!series || change === null || change === 0) return null

      return t(`banking_statistics.facts.reversal_${change > 0 ? 'up' : 'down'}`, {
        series,
        change: signed(change, `${formatNumber(Math.abs(change), 2)} ${t('banking_statistics.unit_pp')}`)
      })
    }
    case 'region_ratio': {
      const regionKey = `banking_statistics.regions.names_in.${fact.params.region}`
      const ratio = numberParam(fact, 'ratio')

      if (!known(regionKey) || ratio === null) return null

      return t('banking_statistics.facts.region_ratio', {
        region_in: t(regionKey),
        ratio: formatNumber(ratio, 2)
      })
    }
    default:
      return null
  }
}

const cards = computed<IFactCard[]>(() =>
  props.facts
    .flatMap((fact): IFactCard[] => {
      const headlineBlock = HEADLINE_FACTS[fact.key]

      if (headlineBlock && props.blocks.includes(headlineBlock)) return []

      const text = factText(fact)

      if (text === null) {
        if (import.meta.dev) console.warn('[banking-statistics] fact without a text', fact)

        return []
      }

      // Templates may start with a lower-case word, such as a region in the locative case.
      const sentence = text.charAt(0).toLocaleUpperCase() + text.slice(1)

      return [
        {
          key: `${fact.key}-${fact.block}-${fact.params.series ?? ''}`,
          block: fact.block,
          text: sentence,
          isLinked: props.blocks.includes(fact.block)
        }
      ]
    })
    .slice(0, MAX_FACTS)
)
</script>

<template>
  <section
    v-if="cards.length"
    data-testid="banking-facts"
    aria-labelledby="banking-facts-title"
    class="space-y-3"
  >
    <h2 id="banking-facts-title" class="text-lg font-semibold">
      {{ t('banking_statistics.facts.title') }}
    </h2>
    <div class="grid gap-3 md:grid-cols-3">
      <template v-for="card in cards" :key="card.key">
        <NuxtLink
          v-if="card.isLinked"
          data-testid="banking-fact"
          :data-block="card.block"
          :to="{ hash: `#${card.block}` }"
          class="group flex flex-col justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4 text-sm leading-relaxed transition-colors hover:border-blue-600"
        >
          <span class="text-gray-900">{{ card.text }}</span>
          <span class="inline-flex items-center gap-1 text-xs font-semibold text-blue-600">
            {{ t('banking_statistics.facts.more') }}
            <LucideArrowDown class="size-3.5 transition-transform group-hover:translate-y-0.5" />
          </span>
        </NuxtLink>
        <div
          v-else
          data-testid="banking-fact"
          class="rounded-lg border border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-900"
        >
          {{ card.text }}
        </div>
      </template>
    </div>
  </section>
</template>
